import { useEffect, useRef, useState } from "react";
import axiosClient from "../axiosClient";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

const AppointmentAlerts = ({ appointments }) => {
  const hasRun = useRef(false);
  const [weekAlerts, setWeekAlerts] = useState([]);
  const [twoDayAlerts, setTwoDayAlerts] = useState([]);
  const [openWeekDialog, setOpenWeekDialog] = useState(false);
  const [openTwoDayDialog, setOpenTwoDayDialog] = useState(false);

  useEffect(() => {
    if (hasRun.current || appointments.length === 0) return;
    hasRun.current = true;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let newTwoDayAlerts = [];
    let newWeekAlerts = [];

    appointments.forEach((appointment) => {
      const appointmentDate = new Date(appointment.appointment_date);
      const daysDifference = Math.floor(
        (appointmentDate - today) / (1000 * 60 * 60 * 24)
      );

      const { reminder_two_day, reminder_week } = appointment.user_reminder || {
        reminder_two_day: false,
        reminder_week: false,
      };


      const startTime = appointment.start
        ? new Date(appointment.start).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
        : "Unknown";
      const endTime = appointment.end
        ? new Date(appointment.end).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
        : "Unknown";

      // Use title if available, otherwise fallback to client_name and client_surname
      const clientName = appointment.title

      if (daysDifference >= 0 && daysDifference <= 2 && !reminder_two_day) {
        newTwoDayAlerts.push(
          `Reminder: ${clientName} has an appointment on ${appointment.appointment_date} from ${startTime} to ${endTime} in less than 2 days!`
        );
        axiosClient
          .patch(`api/user-appointment-reminder/${appointment.id}/`, {
            reminder_two_day: true,
          })
          .catch((error) => {
            console.error(
              `Error updating two-day reminder for appointment ${appointment.id}:`,
              error
            );
          });
      }

      if (daysDifference >= 0 && daysDifference <= 7 && !reminder_week) {
        newWeekAlerts.push(
          `Reminder: ${clientName} has an appointment on ${appointment.appointment_date} from ${startTime} to ${endTime} in less than a week!`
        );
        axiosClient
          .patch(`api/user-appointment-reminder/${appointment.id}/`, {
            reminder_week: true,
          })
          .catch((error) => {
            console.error(
              `Error updating week reminder for appointment ${appointment.id}:`,
              error
            );
          });
      }
    });

    // Set alerts and open dialogs if applicable
    if (newWeekAlerts.length > 0) {
      setWeekAlerts(newWeekAlerts);
      setOpenWeekDialog(true);
    }
    if (newTwoDayAlerts.length > 0) {
      setTwoDayAlerts(newTwoDayAlerts);
      // Only open two-day dialog if no week dialog is open
      if (newWeekAlerts.length === 0) {
        setOpenTwoDayDialog(true);
      }
    }
  }, [appointments]);

  const handleCloseWeekDialog = () => {
    setOpenWeekDialog(false);
    // Open two-day dialog after closing week dialog if there are two-day alerts
    if (twoDayAlerts.length > 0) {
      setOpenTwoDayDialog(true);
    }
  };

  const handleCloseTwoDayDialog = () => {
    setOpenTwoDayDialog(false);
  };

  return (
    <>
      {/* Week Reminder Dialog */}
      <Dialog
        open={openWeekDialog}
        onClose={() => { }}
        disableEscapeKeyDown
        slotProps={{
          backdrop: {
            onClick: (e) => e.stopPropagation(),
          },
        }}
        aria-labelledby="week-reminder-title"
        aria-describedby="week-reminder-description"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            padding: 2,
            maxWidth: 500,
          },
        }}
      >
        <DialogTitle id="week-reminder-title">
          <Typography variant="h6" component="span" color="primary">
            Upcoming Appointments (Within 7 Days)
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: 400, overflowY: "auto" }}>
          <DialogContentText id="week-reminder-description" color="text.primary">
            The following appointments are scheduled within the next week:
          </DialogContentText>
          <List dense>
            {weekAlerts.map((alert, index) => (
              <ListItem key={index}>
                <ListItemText primary={alert} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseWeekDialog}
            variant="contained"
            color="primary"
            sx={{ minWidth: 80 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Two-Day Reminder Dialog */}
      <Dialog
        open={openTwoDayDialog}
        onClose={() => { }} // Prevent closing on backdrop click
        disableEscapeKeyDown // Prevent closing on Escape key
        slotProps={{
          backdrop: {
            onClick: (e) => e.stopPropagation(), // Prevent backdrop click from closing
          },
        }}
        aria-labelledby="two-day-reminder-title"
        aria-describedby="two-day-reminder-description"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            padding: 2,
            maxWidth: 500,
          },
        }}
      >
        <DialogTitle id="two-day-reminder-title">
          <Typography variant="h6" component="span" color="primary">
            Upcoming Appointments (Within 2 Days)
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: 400, overflowY: "auto" }}>
          <DialogContentText id="two-day-reminder-description" color="text.primary">
            The following appointments are scheduled within the next 2 days:
          </DialogContentText>
          <List dense>
            {twoDayAlerts.map((alert, index) => (
              <ListItem key={index}>
                <ListItemText primary={alert} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseTwoDayDialog}
            variant="contained"
            color="primary"
            sx={{ minWidth: 80 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppointmentAlerts;